import React, { useEffect, useRef, useState } from "react";
import { Box, Card, CardContent, CircularProgress, Grid, Stack, Typography } from "@mui/material";
import { fetchWithAuth } from "../../../utils/fetchWithAuth";

const API = process.env.REACT_APP_API_BASE;

function StatBar({ label, value, color }) {
  const pct = Math.max(0, Math.min(100, Number(value) || 0));
  return (
    <Stack spacing={0.5} sx={{ width: "100%" }}>
      <Stack direction="row" justifyContent="space-between" sx={{ fontSize: 13, color: "text.secondary" }}>
        <Typography variant="body2">{label}</Typography>
        <Typography variant="body2">{pct}%</Typography>
      </Stack>
      <Box
        sx={{
          width: "100%",
          height: 10,
          borderRadius: 6,
          backgroundColor: "rgba(0,0,0,0.08)",
          overflow: "hidden",
        }}
      >
        <Box sx={{ width: `${pct}%`, height: "100%", backgroundColor: color, transition: "width 300ms ease" }} />
      </Box>
    </Stack>
  );
}

const palette = {
  sideBlue: "#3A78C5",
  sidePink: "#E56A8A",
  sideDark: "#2F2F2F",
  neutralDark: "#3A3A3A",
  neutralMid: "#9B9B9B",
  neutralLight: "#D6D6D6",
  neutralWarmDark: "#6B6B6B",
  neutralWarmMid: "#A7A29B",
  neutralWarmLight: "#CFC8BF",
  neutralWarmAccent: "#8B8073",
};

const translateLabel = (section, key) => {
  const maps = {
    side: { BRIDE: "Невестата", GROOM: "Младоженецот", BOTH: "Двете страни" },
    age: { ADULT: "Возрасни", CHILD: "Деца", BABY: "Бебиња" },
    relationship: {
      FAMILY: "Семејство",
      FRIEND: "Пријатели",
      COWORKER: "Колеги",
      OTHER: "Друго",
    },
  };
  return maps?.[section]?.[key] || key;
};

export default function GuestsStats() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const sideCanvasRef = useRef(null);
  const ageCanvasRef = useRef(null);
  const relCanvasRef = useRef(null);
  const chartInstancesRef = useRef({});

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await fetchWithAuth(`${API}/analysis`, { method: "GET" });
        if (!res?.ok) throw new Error("Failed to load analysis");
        const data = await res.json();
        setStats(data);
      } catch (err) {
        console.error(err);
        setError("Грешка при вчитување на статистика.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Try to render with Chart.js if available globally (via CDN). Fallback to bars if not.
  const ChartGlobal = typeof window !== 'undefined' ? window.Chart : undefined;

  // Build/destroy charts — must be declared before any early return to satisfy rules-of-hooks
  useEffect(() => {
    // cleanup previous instances
    Object.values(chartInstancesRef.current).forEach((inst) => {
      try { inst?.destroy(); } catch (_) {}
    });
    chartInstancesRef.current = {};

    if (!ChartGlobal || !stats) return; // nothing to render yet

    // translate and prepare data maps
    const side = Object.fromEntries(
      Object.entries(stats.side || {}).map(([k, v]) => [translateLabel('side', k), v])
    );
    const age = Object.fromEntries(
      Object.entries(stats.age || {}).map(([k, v]) => [translateLabel('age', k), v])
    );
    const relationship = Object.fromEntries(
      Object.entries(stats.relationship || {}).map(([k, v]) => [translateLabel('relationship', k), v])
    );

    const buildChart = (canvas, title, dataMap, colors) => {
      if (!canvas) return null;
      const labels = Object.keys(dataMap);
      const values = Object.values(dataMap).map((v) => Number(v) || 0);
      const total = values.reduce((a, b) => a + b, 0) || 1;

      // Lightweight plugin to render percentage labels on slices
      const percentagePlugin = {
        id: 'percentageLabel',
        afterDatasetsDraw(chart) {
          const { ctx } = chart;
          ctx.save();
          ctx.font = '12px sans-serif';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          const meta = chart.getDatasetMeta(0);
          meta.data.forEach((arc, i) => {
            const val = values[i] || 0;
            if (!val) return;
            const pct = Math.round((val / total) * 100);
            // Position roughly in the middle between inner/outer radius
            const { x, y, startAngle, endAngle, innerRadius, outerRadius } = arc.getProps(['x','y','startAngle','endAngle','innerRadius','outerRadius'], true);
            const angle = (startAngle + endAngle) / 2;
            const r = (innerRadius + outerRadius) / 2;
            const tx = x + Math.cos(angle) * r;
            const ty = y + Math.sin(angle) * r;
            ctx.fillStyle = '#333';
            ctx.fillText(pct + '%', tx, ty);
          });
          ctx.restore();
        },
      };
      const chart = new ChartGlobal(canvas, {
        type: 'doughnut',
        data: {
          labels,
          datasets: [{ data: values, backgroundColor: colors, borderWidth: 0 }],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { position: 'bottom' }, title: { display: false, text: title } },
          cutout: '55%',
        },
        plugins: [percentagePlugin],
      });
      return chart;
    };

    chartInstancesRef.current.side = buildChart(
      sideCanvasRef.current,
      'Страна',
      side,
      [palette.sidePink, palette.sideBlue, palette.sideDark]
    );
    chartInstancesRef.current.age = buildChart(
      ageCanvasRef.current,
      'Возраст',
      age,
      [palette.neutralDark, palette.neutralMid, palette.neutralLight]
    );
    chartInstancesRef.current.rel = buildChart(
      relCanvasRef.current,
      'Однос',
      relationship,
      [palette.neutralWarmDark, palette.neutralWarmMid, palette.neutralWarmLight, palette.neutralWarmAccent]
    );

    return () => {
      Object.values(chartInstancesRef.current).forEach((inst) => {
        try { inst?.destroy(); } catch (_) {}
      });
      chartInstancesRef.current = {};
    };
  }, [ChartGlobal, stats]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" my={2}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (error || !stats) return null;

  const side = Object.fromEntries(Object.entries(stats.side || {}).map(([k,v]) => [translateLabel('side', k), v]));
  const age = Object.fromEntries(Object.entries(stats.age || {}).map(([k,v]) => [translateLabel('age', k), v]));
  const relationship = Object.fromEntries(
    Object.entries(stats.relationship || {}).map(([k,v]) => [translateLabel('relationship', k), v])
  );

  const sections = [
    { title: "Страна", key: 'side', data: side, colors: [palette.sidePink, palette.sideBlue, palette.sideDark] },
    { title: "Возраст", key: 'age', data: age, colors: [palette.neutralDark, palette.neutralMid, palette.neutralLight] },
    { title: "Однос", key: 'relationship', data: relationship, colors: [palette.neutralWarmDark, palette.neutralWarmMid, palette.neutralWarmLight, palette.neutralWarmAccent] },
  ];


  return (
    <Grid container spacing={2} sx={{ mb: 2 }} justifyContent="center">
      {sections.map((sec) => (
        <Grid item xs={12} md={4} key={sec.title}>
          <Card sx={{ borderRadius: 2, boxShadow: '0 12px 24px rgba(0,0,0,0.08)' }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 700, textAlign: 'center' }}>{sec.title}</Typography>
              {ChartGlobal ? (
                <Box sx={{ height: 260, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <canvas ref={sec.key === 'side' ? sideCanvasRef : sec.key === 'age' ? ageCanvasRef : relCanvasRef} />
                </Box>
              ) : (
                <Stack spacing={1} sx={{ maxWidth: 360, mx: 'auto' }}>
                  {Object.entries(sec.data).map(([label, value], i) => (
                    <StatBar key={label} label={label} value={value} color={sec.colors[i % sec.colors.length]} />
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
}
